import React from 'react';
import { toast } from 'sonner';

// Okt 2026 — Helper bersama untuk semua halaman chat:
//  1. <LinkifiedText>: link http/https di dalam pesan jadi bisa diklik (tab baru).
//  2. copyMessageText(): menyalin isi pesan (tombol "Salin" di menu tekan lama).
//  3. keepFocusOnPress: dipasang di tombol kirim supaya tidak mengambil fokus
//     dari kolom ketik (keyboard HP tetap terbuka setelah kirim).

// Hanya http/https. Tanda baca di ujung link (titik, koma, kurung) tidak ikut.
const URL_REGEX = /(https?:\/\/[^\s<>"']+)/gi;
const TRAILING_PUNCT = /[.,;:!?)\]}]+$/;

export function LinkifiedText({ text, className = 'underline break-all' }) {
  if (text === null || text === undefined || text === '') return null;
  const str = String(text);
  const parts = str.split(URL_REGEX);
  return (
    <>
      {parts.map((part, i) => {
        // split dengan capture group: indeks ganjil = link.
        if (i % 2 === 1) {
          let trail = (part.match(TRAILING_PUNCT) || [''])[0];
          // ")" di ujung tetap bagian link kalau "(" di dalam link seimbang (mis. Wikipedia).
          while (trail.startsWith(')') && (part.match(/\(/g) || []).length > (part.slice(0, part.length - trail.length).match(/\)/g) || []).length) {
            trail = trail.slice(1);
          }
          const url = trail ? part.slice(0, part.length - trail.length) : part;
          if (!/^https?:\/\/[^\s]+/i.test(url)) return <span key={i}>{part}</span>;
          return (
            <React.Fragment key={i}>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className={className}
                // Jangan picu swipe/long-press/klik area chat di atasnya.
                onClick={(e) => e.stopPropagation()}
              >
                {url}
              </a>
              {trail}
            </React.Fragment>
          );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </>
  );
}

export function copyMessageText(text) {
  const value = (text === null || text === undefined) ? '' : String(text);
  if (!value) {
    toast.error('Tidak ada teks untuk disalin');
    return;
  }
  const fallback = () => {
    try {
      const ta = document.createElement('textarea');
      ta.value = value;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) toast.success('Pesan disalin');
      else toast.error('Gagal menyalin pesan');
    } catch (e) {
      toast.error('Gagal menyalin pesan');
    }
  };
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(value)
      .then(() => toast.success('Pesan disalin'))
      .catch(fallback);
  } else {
    fallback();
  }
}

// Spread ke <button>: mencegah tombol mengambil fokus dari kolom ketik.
// onClick tetap jalan seperti biasa.
export const keepFocusOnPress = {
  onMouseDown: (e) => e.preventDefault(),
};

// Okt 2026 — Pesan sementara (optimistic) tidak boleh terhapus oleh pemuatan
// ulang otomatis (polling) yang hasilnya datang terlambat.
//  - Pesan sementara yang belum dikonfirmasi server: selalu dipertahankan.
//  - Yang sudah dikonfirmasi (_confirmedAt): dipertahankan sampai ada pemuatan
//    yang MULAI setelah konfirmasi (hasil pemuatan itu pasti sudah memuat pesan asli).
const msgText = (m) => (m && (m.content ?? m.message_text ?? m.message)) ?? '';
const msgSender = (m) => (m && (m.sender_user_id ?? m.sender_id)) ?? null;

// Buang pesan sementara yang sudah punya pasangan di pesan asli dari server
// (pengirim dan isi sama). Dicocokkan satu per satu, jadi dua pesan identik
// berturut-turut tetap tampil dua.
export function dropMatchedTemps(temps, newRealMsgs) {
  const pool = [...(newRealMsgs || [])];
  return (temps || []).filter((t) => {
    // Pesan tanpa teks (mis. hanya lampiran) tidak dicocokkan lewat isi.
    if (!String(msgText(t)).trim()) return true;
    const idx = pool.findIndex((r) => {
      if (String(msgText(r)).trim() !== String(msgText(t)).trim()) return false;
      const a = msgSender(r);
      const b = msgSender(t);
      return a == null || b == null || a === b;
    });
    if (idx === -1) return true;
    pool.splice(idx, 1);
    return false;
  });
}

export function mergeOptimistic(serverMsgs, prevMsgs, fetchStartedAt) {
  const base = Array.isArray(serverMsgs) ? serverMsgs : [];
  const prev = Array.isArray(prevMsgs) ? prevMsgs : [];
  const keep = prev.filter(
    (m) => m && m._optimistic && (!m._confirmedAt || fetchStartedAt <= m._confirmedAt)
  );
  if (!keep.length) return base;
  // Pesan asli yang baru muncul (belum ada di tampilan sebelumnya) mungkin pasangan
  // dari pesan sementara: kalau begitu, pesan sementaranya dibuang agar tidak dobel.
  const prevIds = new Set(prev.filter((m) => m && !m._optimistic).map((m) => m.message_id));
  const fresh = base.filter((m) => m && !prevIds.has(m.message_id));
  // Pesan yang sedang menunggu di antrean kirim (_outbox) tidak dibuang lewat pencocokan isi:
  // ia baru hilang saat antrean mengonfirmasi terkirim.
  const loose = new Set(dropMatchedTemps(keep.filter((m) => !m._outbox), fresh));
  const remaining = keep.filter((m) => m._outbox || loose.has(m));
  return remaining.length ? [...base, ...remaining] : base;
}

// Tandai pesan sementara sebagai sudah diterima server.
export function markOptimisticConfirmed(setMessages, messageId) {
  const at = Date.now();
  setMessages((prev) =>
    prev.map((m) => (m.message_id === messageId ? { ...m, _confirmedAt: at } : m))
  );
}

// ===== Pilih beberapa pesan lalu Salin (Okt 2026) =====
// Pakai di halaman chat: const sel = useMessageSelection();
//  - menu tekan lama: tombol "Pilih" -> sel.start(message_id)
//  - baris pesan: {...sel.rowProps(message_id)}  (tap = pilih/batal pilih saat mode pilih aktif)
//  - <SelectionBar count={sel.count} onCopy={() => sel.copy(messages)} onCancel={sel.clear} />
export function selectRowProps(selecting, selected, onToggle, id) {
  if (!selecting) return {};
  return {
    onClickCapture: (e) => {
      e.preventDefault();
      e.stopPropagation();
      onToggle(id);
    },
    style: selected
      ? { background: 'rgba(59,130,246,0.14)', borderRadius: 12, cursor: 'pointer' }
      : { cursor: 'pointer' },
  };
}

function formatCopyTime(ts) {
  const d = new Date(ts);
  if (!ts || Number.isNaN(d.getTime())) return '';
  const date = d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Jakarta' });
  const time = d.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jakarta' });
  return `${date} ${time}`;
}

// Gabungkan pesan terpilih sesuai urutan di layar: "[2 Okt 2026 07.29] Nama: isi".
export function buildSelectedText(messages, selectedIds) {
  const set = new Set(selectedIds);
  const lines = [];
  (messages || []).forEach((m) => {
    if (!m || !set.has(m.message_id) || m.is_deleted) return;
    let text = msgText(m);
    if (!String(text).trim()) text = m.file_name ? `[Lampiran: ${m.file_name}]` : '';
    if (!String(text).trim()) return;
    const when = formatCopyTime(m.timestamp || m.created_at);
    const head = `${when ? `[${when}] ` : ''}${m.sender_name ? `${m.sender_name}: ` : ''}`;
    lines.push(`${head}${text}`);
  });
  return lines.join('\n');
}

export function useMessageSelection(resetKey) {
  const [selectedIds, setSelectedIds] = React.useState([]);
  // Ganti room/channel: pilihan lama dibatalkan.
  React.useEffect(() => { setSelectedIds([]); }, [resetKey]);
  const start = React.useCallback((id) => setSelectedIds([id]), []);
  const toggle = React.useCallback(
    (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])),
    []
  );
  const clear = React.useCallback(() => setSelectedIds([]), []);
  const selecting = selectedIds.length > 0;
  return {
    selecting,
    count: selectedIds.length,
    selectedIds,
    start,
    toggle,
    clear,
    isSelected: (id) => selectedIds.includes(id),
    rowProps: (id) => selectRowProps(selecting, selectedIds.includes(id), toggle, id),
    copy: (messages) => {
      const text = buildSelectedText(messages, selectedIds);
      copyMessageText(text);
      if (text) setSelectedIds([]);
    },
  };
}

export function SelectionBar({ count, onCopy, onCancel }) {
  if (!count) return null;
  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[60] bg-white border-t border-gray-200 shadow-lg flex items-center justify-between px-4 py-3"
      style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}
      data-testid="selection-bar"
    >
      <button type="button" onClick={onCancel} className="text-sm text-gray-600 px-3 py-2" data-testid="selection-cancel">
        Batal
      </button>
      <span className="text-sm text-gray-700">{count} pesan dipilih</span>
      <button
        type="button"
        onClick={onCopy}
        className="text-sm font-medium text-white bg-blue-600 rounded-full px-4 py-2"
        data-testid="selection-copy"
      >
        Salin
      </button>
    </div>
  );
}

// ===== Antrian kirim (Okt 2026) =====
// Pesan langsung tampil dan kolom langsung kosong, tapi pengiriman ke server dilakukan
// satu per satu sesuai urutan ketik supaya urutan di server selalu benar.
// Kiriman yang macet lebih dari 2 menit dilepas dari antrian (antrian lanjut).
export function useSendQueue() {
  const chain = React.useRef(Promise.resolve());
  return React.useCallback((task) => {
    const run = chain.current.then(() =>
      Promise.race([
        Promise.resolve().then(task),
        new Promise((resolve) => setTimeout(resolve, 120000)),
      ])
    );
    chain.current = run.catch(() => {});
    return run;
  }, []);
}
